import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Grid, Divider as MuiDivider, Typography as MuiTypography } from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { Helmet } from "react-helmet-async";

import CustomDataGrid from "./CustomDataGrid";
import useReports from "../../../hooks/useReports";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp, roundOff, formatDate } from "../../../utils/common";
import Stats from "./../../../components/Stats";
import {
  CurrencyRupee as CurrencyRupeeIcon,
  ReceiptLong as ReceiptLongIcon,
  AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";

const Divider = styled(MuiDivider)(spacing);
const Typography = styled(MuiTypography)(spacing);

function RenewalReports() {
  const { getRenewalReports, isLoading, renewalReports, downloadRenewalReport } = useReports();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openDialog } = useDialog();

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  const today = formatDate(new Date());

  const currentPayload = {
    filters: [
      {
        fieldName: "invoiceStatus",
        fieldValue: "UP,PA",
        op: "in",
        dataType: "string",
        condition: "and",
        isJoin: false,
      },
      {
        fieldName: "invoiceDate",
        fieldValue: `${today} 00:00:00,${today} 23:59:59`,
        op: "btn",
        dataType: "date",
        condition: "and",
        isJoin: false,
      },
    ],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [
      {
        field: "createdAt",
        sort: "desc",
      },
    ],
  };

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);

  const [filterFormData, setFilterFormData] = useState({
    status: [
      {
        label: "Un Paid",
        value: "UP",
      },
      {
        label: "Paid",
        value: "PA",
      },
    ],
    invoiceDate: [new Date(), new Date()],
    invoiceDateStr: `${today} 00:00:00,${today} 23:59:59`,
    planName: [],
  });

  const [payload, setPayload] = useState(currentPayload);

  const fetchData = async () => {
    await getRenewalReports(payload);
  };

  const refreshPage = () => {
    setPayload(currentPayload);
    setFilterFormData({
      status: [
        {
          label: "Un Paid",
          value: "UP",
        },
        {
          label: "Paid",
          value: "PA",
        },
      ],
      invoiceDate: [new Date(), new Date()],
      invoiceDateStr: `${today} 00:00:00,${today} 23:59:59`,
    });
    filterResetFunc();
    setPaginationModel({
      page: 0,
      pageSize: 100,
    });
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");

    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize, // Update pageSize dynamically
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  useEffect(() => {
    fetchData();
  }, [payload]);

  const exportFile = async (fileType) => {
    await downloadRenewalReport(fileType, payload);
  };

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  return (
    <React.Fragment>
      <Helmet title="Renewal Reports" />
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid item>
          <Typography variant="h4" gutterBottom>
            Renewal Statistics
          </Typography>
        </Grid>
      </Grid>
      <Divider my={4} />
      <Grid container spacing={6}>
        <Grid item xs={12} sm={12} md={6} lg={4} xl>
          <Stats
            title="Plan Amount"
            amount={"₹" + roundOff(renewalReports.renewalReportSum.planAmount)}
            chip="Weekly"
            bgcolor="#3b82f6"
            icon={CurrencyRupeeIcon}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={4} xl>
          <Stats
            title="Service Tax Amount"
            amount={"₹" + roundOff(renewalReports.renewalReportSum.serviceTaxAmount)}
            chip="Annual"
            bgcolor="#8b5cf6"
            icon={ReceiptLongIcon}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={4} xl>
          <Stats
            title="Total Payable Amount"
            amount={"₹" + roundOff(renewalReports.renewalReportSum.totalPayableAmount)}
            chip="Weekly"
            bgcolor="#10b981"
            icon={WalletIcon}
          />
        </Grid>
      </Grid>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <CustomDataGrid
            trans={t}
            addPage={null}
            editPage={null}
            refreshPage={refreshPage}
            downloadFile={exportFile}
            openDownloadDialog={null}
            id="renewId"
            data={renewalReports.data}
            rowCount={renewalReports.paging.totalNumberOfRecords}
            paging={{
              page: renewalReports.paging.pageNumber,
              pageSize: renewalReports.paging.pageSize,
            }}
            onPaginationModelChange={onPageChange}
            isLoading={isLoading}
            sortModel={sortModel}
            handleSortModelChange={handleSortModelChange}
            setPayload={setPayload}
            payload={payload}
            setFilterResetFunc={setFilterResetFunc}
            filterFormData={filterFormData}
            setFilterFormData={setFilterFormData}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default RenewalReports;
